import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";

interface EmptyStateProps {
  /** Lucide icon element, e.g. <Building2 className="h-6 w-6" />. */
  icon?: ReactNode;
  title: string;
  description?: string;
  /** Primary call to action. Omit for a passive empty state (e.g. filtered). */
  action?: { label: string; onClick: () => void; icon?: ReactNode };
  className?: string;
}

/**
 * Consistent empty-state block: a muted icon chip, a title, an optional
 * one-line description, and an optional primary action. Used across list
 * pages so a genuinely-empty page reads as "here's what to do next" instead
 * of a dead-end line of grey text.
 *
 * Loading states are handled by the caller (show "Loading…" while fetching);
 * this component is for the genuine-empty / no-results case.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center px-6 py-14 text-center",
        className,
      )}
    >
      {icon && (
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-soft text-brand-primary">
          {icon}
        </div>
      )}
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      {description && (
        <p className="mt-1 max-w-sm text-xs text-slate-500">{description}</p>
      )}
      {action && (
        <Button onClick={action.onClick} className="mt-4" size="sm">
          {action.icon}
          {action.label}
        </Button>
      )}
    </div>
  );
}
